/**
 * IndexedDB-backed artwork image storage.
 * Mirrors photo-db.ts but in a separate DB so art and photos can be cleared
 * independently. Stores the canvas dataUrl outside localStorage so the
 * persisted art store can stay under the ~5MB origin quota.
 * Artwork records reference the stored image as "idb::{id}" in `Artwork.dataUrl`.
 */

const DB_NAME = 'homeschool-art-images';
const STORE_NAME = 'artworks';
const DB_VERSION = 1;

export const ART_IDB_PREFIX = 'idb::';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Save a base64 data URL to IndexedDB under the given key. Returns the "idb::{id}" reference. */
export async function saveArt(id: string, dataUrl: string): Promise<string> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(dataUrl, id);
    tx.oncomplete = () => resolve(`${ART_IDB_PREFIX}${id}`);
    tx.onerror = () => reject(tx.error);
  });
}

/** Retrieve a base64 data URL from IndexedDB. Returns null if not found. */
export async function getArt(id: string): Promise<string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve((req.result as string) ?? null);
    req.onerror = () => reject(req.error);
  });
}

/** Delete a single artwork image by key. */
export async function deleteArt(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Extract the IndexedDB key from an "idb::"-prefixed dataUrl reference. */
export function artKey(url: string): string {
  return url.startsWith(ART_IDB_PREFIX) ? url.slice(ART_IDB_PREFIX.length) : url;
}

/** Check if a dataUrl is an IndexedDB reference rather than an inline data URL. */
export function isIdbArt(url: string): boolean {
  return url.startsWith(ART_IDB_PREFIX);
}
